import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import AppFlatpickr from "components/common/AppFlatpickr";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getLeaveTypeOptions } from "../services/LeaveTypeService";
import { createLeaveApplication } from "../services/LeaveApplicationService";
import { LeaveSummaryPanel } from "../components/LeaveSummaryPanel";
import { getDocumentTitle } from "utils/helpers";
import { useProfile } from "features/Authentication/hooks/useProfile";
import {
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import schema from "./schema";
import { getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const LeaveApplicationForm = () => {
  document.title = getDocumentTitle("New leave application");
  const navigate = useNavigate();
  const { isSuperAdmin, isHR, user } = useProfile();
  const canSelectUser = isSuperAdmin || isHR;
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  const validation = useFormik({
    initialValues: {
      leaveTypeId: "" as number | "",
      fromDate: toYmd(new Date()),
      toDate: "",
      isHalfDay: false,
      reason: "",
      userId: canSelectUser
        ? ("" as number | "")
        : ((user?.id ?? "") as number | ""),
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const res = await createLeaveApplication({
          leaveTypeId: Number(values.leaveTypeId),
          fromDate: values.fromDate,
          toDate: values.isHalfDay ? values.fromDate : values.toDate,
          isHalfDay: values.isHalfDay || undefined,
          reason: values.reason.trim(),
          userId: Number(values.userId),
        });

        if (res.success) {
          toast.success(res.message ?? "Leave application created.");
          navigate(-1);
        } else {
          toast.error(res.message ?? "Could not create application.");
        }
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not create application.",
        );
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="New leave application"
          pageTitle="HR Management"
          backTo={{
            label: "Leave applications",
            path: "/hr-management/leave-applications",
          }}
        />
        <Row className="justify-content-center">
        <Col xl={10} lg={10}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">Apply for leave</h5>
              <p className="text-muted small mb-0 mt-1">
                {canSelectUser
                  ? "Choose an employee, leave type, and inclusive date range."
                  : "Choose leave type and inclusive date range. The application is created for your employee profile."}
              </p>
            </CardHeader>
            <CardBody>
              <Row className="g-3 ">
                <Col xs={12} lg={6}>
                  <Row className="g-3">
                    {canSelectUser ? (
                      <Col xs={12}>
                        <Label className="form-label">
                          Employee <span className="text-danger">*</span>
                        </Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getUserOptionsWithEmployeeProfile}
                          placeholder="Search employees…"
                          value={validation.values.userId}
                          onChange={(opt: any) => {
                            const v =
                              opt && !Array.isArray(opt) ? opt.value : undefined;
                            void validation.setFieldValue(
                              "userId",
                              v != null ? v : "",
                            );
                            setSelectedEmployeeId(
                              opt && !Array.isArray(opt) && opt.employeeId
                                ? Number(opt.employeeId)
                                : null,
                            );
                          }}
                          onBlur={() => validation.setFieldTouched("userId", true)}
                          isInvalid={
                            validation.touched.userId && !!validation.errors.userId
                          }
                          components={{
                            Option: EmployeeOption,
                            SingleValue: EmployeeSingleValue,
                          }}
                        />
                        {validation.touched.userId && validation.errors.userId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(validation.errors.userId)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    ) : null}
                    <Col xs={12}>
                      <Label className="form-label">
                        Leave type <span className="text-danger">*</span>
                      </Label>
                      <AsyncPaginatedSelect
                        fetchOptions={getLeaveTypeOptions}
                        placeholder="Search leave types…"
                        value={validation.values.leaveTypeId}
                        onChange={(opt: any) => {
                          const v =
                            opt && !Array.isArray(opt) ? opt.value : undefined;
                          validation.setFieldValue(
                            "leaveTypeId",
                            v != null ? v : "",
                          );
                        }}
                        onBlur={() =>
                          validation.setFieldTouched("leaveTypeId", true)
                        }
                        isInvalid={
                          validation.touched.leaveTypeId &&
                          !!validation.errors.leaveTypeId
                        }
                      />
                      {validation.touched.leaveTypeId &&
                      validation.errors.leaveTypeId ? (
                        <FormFeedback type="invalid" className="d-block">
                          {String(validation.errors.leaveTypeId)}
                        </FormFeedback>
                      ) : null}
                    </Col>
                  </Row>
                </Col>

                <Col xs={12}>
                  <hr className="my-4 text-muted opacity-25" />

                  <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                    Duration
                  </div>
                </Col>
                <Col md={6}>
                  <Label className="form-label">
                    From <span className="text-danger">*</span>
                  </Label>
                  <AppFlatpickr
                    className={`form-control ${
                      validation.touched.fromDate && validation.errors.fromDate
                        ? "is-invalid"
                        : ""
                    }`}
                    options={{
                      altInput: true,
                      altFormat: "d M, Y",
                      dateFormat: "Y-m-d",
                      onClose: () =>
                        validation.setFieldTouched("fromDate", true),
                    }}
                    onChange={(dates: Date[]) => {
                      const d = dates?.[0];
                      const ymd = d ? toYmd(d) : "";
                      void validation.setFieldValue("fromDate", ymd);
                      if (validation.values.isHalfDay) {
                        void validation.setFieldValue("toDate", ymd);
                      }
                    }}
                    value={
                      validation.values.fromDate
                        ? new Date(`${validation.values.fromDate}T12:00:00`)
                        : ""
                    }
                    placeholder="Enter Start Date"
                  />
                  {validation.touched.fromDate && validation.errors.fromDate ? (
                    <FormFeedback type="invalid" className="d-block">
                      {String(validation.errors.fromDate)}
                    </FormFeedback>
                  ) : null}
                </Col>
                <Col md={6}>
                  <Label className="form-label">
                    To <span className="text-danger">*</span>
                  </Label>
                  <AppFlatpickr
                    className={`form-control ${
                      validation.touched.toDate && validation.errors.toDate
                        ? "is-invalid"
                        : ""
                    }`}
                    options={{
                      altInput: true,
                      altFormat: "d M, Y",
                      dateFormat: "Y-m-d",
                      minDate: validation.values.fromDate || undefined,
                      maxDate: validation.values.isHalfDay && validation.values.fromDate
                        ? new Date(`${validation.values.fromDate}T12:00:00`)
                        : (() => {
                            if (!validation.values.fromDate) return undefined;
                            const d = new Date(`${validation.values.fromDate}T12:00:00`);
                            d.setMonth(d.getMonth() + 6);
                            return d;
                          })(),
                      onClose: () => validation.setFieldTouched("toDate", true),
                    }}
                    onChange={(dates: Date[]) => {
                      const d = dates?.[0];
                      void validation.setFieldValue("toDate", d ? toYmd(d) : "");
                    }}
                    placeholder="Enter End Date"
                    value={
                      validation.values.toDate
                        ? new Date(`${validation.values.toDate}T12:00:00`)
                        : ""
                    }
                    disabled={validation.values.isHalfDay}
                  />
                  {validation.touched.toDate && validation.errors.toDate ? (
                    <FormFeedback type="invalid" className="d-block">
                      {String(validation.errors.toDate)}
                    </FormFeedback>
                  ) : null}
                </Col>
                <Col xs={12}>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="form-check form-switch mb-0">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="isHalfDay"
                        checked={validation.values.isHalfDay}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          void validation.setFieldValue("isHalfDay", checked);
                          if (checked && validation.values.fromDate) {
                            void validation.setFieldValue("toDate", validation.values.fromDate);
                          }
                        }}
                      />
                      <Label className="form-check-label mb-0" htmlFor="isHalfDay">
                        Half day
                      </Label>
                    </div>
                    {validation.values.isHalfDay ? (
                      <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                        0.5 days
                      </span>
                    ) : null}
                  </div>
                </Col>
                <Col xs={12}>
                  <LeaveSummaryPanel
                    fromDate={validation.values.fromDate || null}
                    toDate={validation.values.toDate || null}
                    employeeId={selectedEmployeeId}
                  />
                </Col>
                <Col xs={12}>
                  <Label className="form-label">
                    Reason <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    rows={3}
                    name="reason"
                    value={validation.values.reason}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    placeholder="Provide a reason for the leave request"
                    invalid={
                      validation.touched.reason && !!validation.errors.reason
                    }
                  />
                  {validation.touched.reason && validation.errors.reason ? (
                    <FormFeedback type="invalid">
                      {String(validation.errors.reason)}
                    </FormFeedback>
                  ) : null}
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-2 justify-content-end mt-4">
                <BackButton />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={validation.isSubmitting}
                >
                  {validation.isSubmitting
                    ? "Submitting…"
                    : "Submit application"}
                </button>
              </div>
            </CardBody>
          </Card>
        </Form>
        </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LeaveApplicationForm;
